import type {
  CarrefourReferentialIntervalType,
  DateReferentialIntervalType,
  ReferentialIntervalsType
} from '@carrefour-gcs/shared';
import type { PeriodGranularityType, PeriodReferentialIntervalType } from '../types/period.type';
import { customDateToLocalDayjs } from './periodDate.utils';

type IntervalsByGranularity = Partial<Record<PeriodGranularityType, DateReferentialIntervalType>>;

/**
 * Boundaries of the available data for one granularity. Falls back on the standard intervals
 * when the requested key does not define the granularity: the competitor vision has no daily interval.
 */
export function getPeriodReferentialInterval(
  referentialIntervals: ReferentialIntervalsType | undefined,
  key: keyof CarrefourReferentialIntervalType,
  granularity: PeriodGranularityType
): PeriodReferentialIntervalType | null {
  const carrefour = referentialIntervals?.carrefour;

  if (!carrefour) {
    return null;
  }

  const requested: IntervalsByGranularity | undefined = carrefour[key];
  const interval = requested?.[granularity] ?? carrefour.standard[granularity];

  if (!interval) {
    return null;
  }

  const start = customDateToLocalDayjs(interval.start).startOf('day');
  const end = customDateToLocalDayjs(interval.end).endOf('day');

  // A malformed API date (e.g. an out-of-range month) must not surface as an "Invalid Date" label downstream.
  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  return { start, end };
}
